// /app/frontend/src/contexts/LanguageContext.tsx

import React, { createContext, useContext, useState, useEffect } from 'react';
import i18n from 'i18next';
import { useAppSettings } from './AppSettingsContext'; // Import useAppSettings hook

interface LanguageContextType {
    language: string;
    setLanguage: (language: string) => void;
}

const initialState: LanguageContextType = {
    language: 'en', // Default to 'en' or you can choose to keep it empty
    setLanguage: () => { },
};

const LanguageContext = createContext<LanguageContextType>(initialState);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { settings, appSettingsReady, error } = useAppSettings(); // Destructure AppSettings
    const [language, setLanguageState] = useState<string>(initialState.language);

    // Initialize language based on AppSettings.defaultLanguage once settings are ready
    useEffect(() => {
        if (appSettingsReady && settings) {
            setLanguageState(settings.defaultLanguage);
            i18n.changeLanguage(settings.defaultLanguage);
        }
    }, [appSettingsReady, settings]);

    // Listen to language changes in i18next and update state accordingly
    useEffect(() => {
        const handleLanguageChange = (lng: string) => {
            setLanguageState(lng);
        };

        i18n.on('languageChanged', handleLanguageChange);

        return () => {
            i18n.off('languageChanged', handleLanguageChange);
        };
    }, []);

    // Function to change language
    const handleSetLanguage = (language: string) => {
        i18n.changeLanguage(language); // This will trigger the 'languageChanged' event
    };

    return (
        <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage }}>
            {children}
        </LanguageContext.Provider>
    );
};

export const useLanguage = () => useContext(LanguageContext);
